<template>
	<div class="buybox-filter">
		<HiddenHorizontalScrollbar>
			<label v-for="{ value, label } of filterOptions" :key="value">
				<button
					class="buybox-filter__label"
					:class="{
						'buybox-filter__label--active': value === buyboxFilterType,
					}"
					@click="handleFilterClick(value)"
				>
					{{ label }}
				</button>
			</label>
		</HiddenHorizontalScrollbar>
	</div>
</template>

<script setup lang="ts">
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const HiddenHorizontalScrollbar = defineAsyncComponent(() => import('@/components/HiddenHorizontalScrollbar.vue'));

const { t } = useI18n();

const { activeRoute } = storeToRefs(useRoutingStore());
const props = defineProps<{
	buyBoxFilterOptions: BuyBoxFilterOptions[];
	additionalContexts: SnowplowContext[];
	isTitleUpcoming: boolean;
	buyboxFilterType: BuyBoxFilterOptions;
}>();

const emit = defineEmits<{
	(
		e: 'filterClicked',
		{
			value,
			nonInteraction,
		}: {
			value: BuyBoxFilterOptions;
			nonInteraction?: boolean;
		}
	): void;
}>();

const filterOptions = computed(() => {
	const options = [
		{
			label: t('WEBAPP_ALL'),
			value: BuyBoxFilterOptions.ALL,
		},
		{
			label: t('WEBAPP_SUBSCRIPTION'),
			value: BuyBoxFilterOptions.SUBSCRIPTION,
		},
		{
			label: t('WEBAPP_FREE'),
			value: BuyBoxFilterOptions.FREE,
		},
		{
			label: t('WEBAPP_FREE_TV'),
			value: BuyBoxFilterOptions.FAST_TV,
		},
		{
			label: t('WEBAPP_RENT'),
			value: BuyBoxFilterOptions.RENT,
		},
		{
			label: t('WEBAPP_BUY'),
			value: BuyBoxFilterOptions.BUY,
		},
		{
			label: t('WEBAPP_CINEMA'),
			value: BuyBoxFilterOptions.CINEMA,
		},
		{
			label: t('WEBAPP_DISC'),
			value: BuyBoxFilterOptions.PHYSICAL,
		},
	];

	return options.filter(option => {
		return (
			props.buyBoxFilterOptions.includes(option.value) ||
			[
				BuyBoxFilterOptions.ALL,
				props.isTitleUpcoming ? BuyBoxFilterOptions.SUBSCRIPTION : null,
				BuyBoxFilterOptions.FREE,
			].includes(option.value)
		);
	});
});

const handleFilterClick = (value: BuyBoxFilterOptions) => {
	emit('filterClicked', { value });
};

onMounted(() => {
	if (activeRoute.value?.query.monetization_types === 'free') {
		emit('filterClicked', { value: BuyBoxFilterOptions.FREE, nonInteraction: true });
		return;
	}

	if (!filterOptions.value.find(filterOption => filterOption.value === props.buyboxFilterType)) {
		emit('filterClicked', { value: BuyBoxFilterOptions.ALL, nonInteraction: true });
		return;
	}
});
</script>

<style lang="scss" scoped>
.search-field-v3 {
	margin-bottom: 24px;
	height: 40px !important;
}
.buybox-filter {
	margin-bottom: 24px;

	&__label {
		display: flex;
		height: 40px;
		padding: 8px 16px;
		border-radius: 24px;
		background: #111924;
		margin-inline-end: 8px;
		color: #6f7d90;
		font-size: 16px;
		letter-spacing: 0.24px;
		border-color: transparent;

		&--active {
			background: #222c38;
			color: #fff;
			font-size: 16px;
			letter-spacing: 0.24px;
		}

		&:hover {
			cursor: pointer;
		}
	}
}
</style>
